// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the lesson panel and Tools share one column (owner, 2026-10-11).
 *
 * On a desktop window the lesson panel, the Khipu player and the room's
 * card stand at the inline-end edge, and the Tools popover hangs from its
 * bar at the foot of the same edge. Stacked side by side they left half
 * the column empty: the lesson pushed aside at the top, Tools alone at the
 * bottom. While both are open they now share Tools' column instead: the
 * panel on top, Tools under it, as wide as each other, each as tall as it
 * needs while both fit and scrolling inside its share when they do not.
 *
 * This module decides when they share (`COLUMN_CLASS` on the body, which
 * the stylesheets and globePanelOffset.ts read) and sets both heights. The
 * Khipu editor is a workbench, not something read beside Tools: it keeps
 * standing beside them.
 */

import { fitSidePanel, SIDE_PANEL_MIN_WIDTH } from './floatingPanel'

/** On the body while a panel shares Tools' column. */
export const COLUMN_CLASS = 'lesson-tools-column'
/** The panels that share it: the lesson panel, the Khipu player, the room's card. */
export const SHARES_COLUMN = '.lesson-player:not(.khipu-editor):not(.game-panel):not(.xr-phone-adopted):not(.lesson-leaving)'

/** The least either panel is given when both need more than half. */
const MIN_SHARE = 160

/**
 * How tall each panel may be in a column `available` px tall with `gap`
 * between them: each as tall as it needs while both fit; else the one that
 * needs less than half has what it needs and the other the rest; else half
 * each. Whole pixels.
 */
export function splitColumn(
  available: number,
  lessonNeed: number,
  toolsNeed: number,
  gap: number,
): { lesson: number; tools: number } {
  const room = Math.max(0, available - gap)
  const half = room / 2
  let lesson: number
  let tools: number
  if (lessonNeed + toolsNeed <= room) {
    lesson = room - toolsNeed
    tools = room - lessonNeed
  } else if (lessonNeed <= half) {
    lesson = lessonNeed
    tools = room - lessonNeed
  } else if (toolsNeed <= half) {
    tools = toolsNeed
    lesson = room - toolsNeed
  } else {
    lesson = tools = half
  }
  return { lesson: Math.max(MIN_SHARE, Math.floor(lesson)), tools: Math.max(MIN_SHARE, Math.floor(tools)) }
}

/** How tall a panel would be with nothing scrolled: its box plus what its scrolling children hide. */
function naturalHeight(panel: HTMLElement): number {
  let hidden = 0
  for (const child of Array.from(panel.children)) {
    if (child instanceof HTMLElement) hidden += Math.max(0, child.scrollHeight - child.clientHeight)
  }
  return panel.offsetHeight + hidden
}

const drawn = (el: Element): boolean => el.getClientRects().length > 0

export interface LessonToolsColumnHandle {
  refresh(): void
  dispose(): void
}

export function initLessonToolsColumn(): LessonToolsColumnHandle {
  /** The panel and the popover heights were set on, to give back when they stop sharing. */
  let sharing: HTMLElement | null = null
  let popover: HTMLElement | null = null
  let frame = 0

  const setMax = (el: HTMLElement, px: string): void => {
    if (el.style.maxHeight !== px) el.style.maxHeight = px
  }

  const refresh = (): void => {
    frame = 0
    const tools = document.getElementById('tools-menu-popover')
    const panel = document.querySelector<HTMLElement>(SHARES_COLUMN)
    const active =
      window.innerWidth >= SIDE_PANEL_MIN_WIDTH &&
      !!tools && !tools.classList.contains('hidden') && drawn(tools) &&
      !!panel && drawn(panel)

    if (!active || panel !== sharing || tools !== popover) {
      // Give back what was set: the popover's height to its stylesheet,
      // the panel's to its own fit (clear of the transport).
      if (popover) popover.style.maxHeight = ''
      const was = sharing
      sharing = popover = null
      if (document.body.classList.contains(COLUMN_CLASS) && !active) document.body.classList.remove(COLUMN_CLASS)
      if (was?.isConnected) fitSidePanel(was)
    }
    if (!active) {
      if (watched) watch()
      return
    }
    sharing = panel
    popover = tools
    // Width and edge first (stylesheets), so the heights are measured as they will stand.
    // Only when missing: adding a class already there is still a mutation, and this observer would go round.
    if (!document.body.classList.contains(COLUMN_CLASS)) document.body.classList.add(COLUMN_CLASS)

    const scale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1
    const gap = Math.round(8 * scale)
    const available = tools.getBoundingClientRect().bottom - panel.getBoundingClientRect().top
    const { lesson, tools: toolsMax } = splitColumn(available, naturalHeight(panel), naturalHeight(tools), gap)
    setMax(panel, `${lesson}px`)
    setMax(tools, `${toolsMax}px`)
    if (watched !== sharing) watch()
  }

  /** A picture loading or the window resizing: measure once per frame. */
  const schedule = (): void => {
    if (!frame) frame = requestAnimationFrame(refresh)
  }

  // Panels come and go as children of the body (and a meeting's audience
  // hides them by the body's class); Tools opens by class in its bar; a
  // step, a tab, a folded section or a picture loading changes a height.
  // Not `style`: this module writes it. At once, not a frame later: the
  // class must be in place when globePanelOffset.ts measures the same
  // change (this module starts first, so its observer is told first).
  const inside = { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'open'] }
  const observer = new MutationObserver(() => {
    if (frame) cancelAnimationFrame(frame)
    refresh()
  })
  let watched: HTMLElement | null = null
  const watch = (): void => {
    observer.disconnect()
    observer.observe(document.body, { childList: true, attributes: true, attributeFilter: ['class'] })
    const bar = document.getElementById('map-controls')
    if (bar) observer.observe(bar, inside)
    watched = sharing
    if (watched) observer.observe(watched, inside)
  }
  watch()
  const onLoad = (e: Event): void => {
    if (sharing && e.target instanceof Node && sharing.contains(e.target)) schedule()
  }
  document.addEventListener('load', onLoad, true)
  window.addEventListener('resize', schedule)
  refresh()

  return {
    refresh,
    dispose(): void {
      observer.disconnect()
      document.removeEventListener('load', onLoad, true)
      window.removeEventListener('resize', schedule)
      if (frame) cancelAnimationFrame(frame)
      if (popover) popover.style.maxHeight = ''
      if (sharing?.isConnected) fitSidePanel(sharing)
      sharing = popover = null
      document.body.classList.remove(COLUMN_CLASS)
    },
  }
}
